import axios from 'axios';
import { ApiError } from '../types/common.type';
import { PUBLIC_ENDPOINTS, API_CONFIG } from '../config/api.config';
import { clearAllAuthData } from '@/utils/auth.utils';
import { clearAuthCookies } from '@/app/actions/auth.actions';
import { logger } from '@/utils/logger';
import { getOrCreateDeviceId } from '@/utils/device-id';

class ApiClient {
  private client;
  private queryClient: any = null;

  constructor() {
    this.client = axios.create({
      baseURL: '/api/proxy',
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  public setQueryClient(client: any) {
    this.queryClient = client;
  }

  private isPublicEndpoint(url?: string): boolean {
    if (!url) return false;
    return PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));
  }

  private isLogoutEndpoint(url?: string): boolean {
    if (!url) return false;
    return url.includes('logout');
  }

  private setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        logger.log(`[API Client] ${config.method?.toUpperCase()} ${config.url}`);
        config.headers['x-device-id'] = getOrCreateDeviceId();
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const isPublic = this.isPublicEndpoint(error.config?.url);
        const isLogout = this.isLogoutEndpoint(error.config?.url);

        // Only auto-logout on 401 for protected endpoints (NOT logout or public endpoints)
        if (error.response?.status === 401 && !isPublic && !isLogout) {
          logger.log('🔒 401 Unauthorized on protected endpoint - Session expired');
          await this.handleLogout(true); // ✅ Pass true = session expired
        }

        if (error.response?.status === 401 && isPublic) {
          logger.log('401 on public endpoint:', error.config?.url, '- Invalid credentials');
        }

        const enhancedError = {
          ...error,
          message:
            error.response?.data?.message || error.message || 'An error occurred',
          statusCode: error.response?.status,
          errors: error.response?.data?.errors,
        };

        return Promise.reject(enhancedError);
      }
    );
  }

  // ✅ Add isSessionExpired parameter
  private async handleLogout(isSessionExpired: boolean = false) {
    if (typeof window === 'undefined') return;

    logger.log('🔒 [ApiClient] Logout triggered', { isSessionExpired });

    // Clear client-side data
    clearAllAuthData();

    // Clear server-side cookies
    await clearAuthCookies();

    // Clear React Query cache
    if (this.queryClient) {
      logger.log('Clearing React Query cache');
      this.queryClient.clear();
    }

    // ✅ Only dispatch event if session actually expired (not manual logout)
    if (isSessionExpired) {
      logger.log('🔒 Dispatching session-expired event');
      window.dispatchEvent(new CustomEvent('session-expired'));
    } else {
      logger.log('✅ Manual logout - no session-expired event');
    }
  }

  public async get<T>(url: string, config = {}) {
    return this.client.get<T>(url, config);
  }

  public async post<T>(url: string, data?: any, config = {}) {
    return this.client.post<T>(url, data, config);
  }

  public async put<T>(url: string, data?: any, config = {}) {
    return this.client.put<T>(url, data, config);
  }

  public async patch<T>(url: string, data?: any, config = {}) {
    return this.client.patch<T>(url, data, config);
  }

  public async delete<T>(url: string, config = {}) {
    return this.client.delete<T>(url, config);
  }
}

export const apiClient = new ApiClient();
